import { TreasureService } from '../services/treasure';
import type { Treasure, AvailableCity } from '../types';

export class TreasureInventory {
  private treasureService = new TreasureService();
  private treasures: Treasure[] = [];
  private availableCities: AvailableCity[] = []; // Now municipalities only
  private selectedMunicipalityCities: AvailableCity[] = []; // Cities within selected municipality
  private placeTypeLabel: string = 'Municipality'; // Dynamic label from country config

  async loadTreasures(partnerId: string, forceRefresh = false): Promise<void> {
    try {
      this.treasures = await this.treasureService.getPartnerTreasures(partnerId, forceRefresh);
    } catch (error) {
      console.error('Failed to load treasures:', error);
      throw error;
    }
  }

  async loadCities(): Promise<void> {
    try {
      this.availableCities = await this.treasureService.getAvailableCities();
    } catch (error) {
      console.error('Failed to load available cities:', error);
      throw error;
    }
  }

  async loadPlaceTypeLabel(): Promise<void> {
    try {
      this.placeTypeLabel = await this.treasureService.getPlaceTypeLabel();
    } catch (error) {
      console.error('Failed to load place type label:', error);
      this.placeTypeLabel = 'Location'; // Fallback
    }
  }

  renderInventory(partnerId: string, onReload?: () => void): HTMLElement {
    const container = document.createElement('div');
    container.className = 'treasure-inventory';

    const title = document.createElement('h3');
    title.textContent = 'My Merchant Tokens';
    container.appendChild(title);

    const reloadButton = document.createElement('button');
    reloadButton.textContent = 'Refresh';
    reloadButton.className = 'reload-btn';
    reloadButton.addEventListener('click', async () => {
      reloadButton.disabled = true;
      reloadButton.textContent = 'Refreshing...';
      try {
        await this.loadAndRender(partnerId, container, onReload, true);
      } finally {
        reloadButton.disabled = false;
        reloadButton.textContent = 'Refresh';
      }
    });
    container.appendChild(reloadButton);

    const treasureList = document.createElement('div');
    treasureList.className = 'treasure-list';
    container.appendChild(treasureList);

    // Load data immediately
    this.loadAndRender(partnerId, container, onReload);

    return container;
  }

  private async loadAndRender(partnerId: string, container: HTMLElement, onReload?: () => void, forceRefresh = false): Promise<void> {
    const treasureList = container.querySelector('.treasure-list') as HTMLElement;
    treasureList.innerHTML = '<div class="loading">Loading treasures...</div>';

    try {
      console.log('Loading treasures for partner:', partnerId);
      
      // Load treasures, cities, and place type label
      await Promise.all([
        this.loadTreasures(partnerId, forceRefresh),
        this.loadCities(),
        this.loadPlaceTypeLabel()
      ]);
      
      treasureList.innerHTML = '';

      if (this.treasures.length === 0) {
        treasureList.innerHTML = '<div class="no-treasures">No merchant tokens purchased yet.</div>';
        return;
      }

      console.log(`Found ${this.treasures.length} treasure(s)`);
      for (const treasure of this.treasures) {
        const treasureCard = await this.renderTreasureCard(treasure);
        treasureList.appendChild(treasureCard);
      }

      if (onReload) onReload();
    } catch (error) {
      console.error('Failed to load treasures:', error);
      treasureList.innerHTML = '<div class="error">Failed to load treasures. Please try again.</div>';
    }
  }

  private async renderTreasureCard(treasure: Treasure): Promise<HTMLElement> {
    const card = document.createElement('div');
    card.className = 'treasure-card';
    card.dataset.treasureId = treasure.id;

    // Create card header with gem icon and status
    const header = document.createElement('div');
    header.className = 'treasure-header';
    
    const gemIcon = document.createElement('div');
    gemIcon.className = 'gem-icon';
    gemIcon.innerHTML = '💎';
    
    const status = document.createElement('span');
    status.className = `status status-${treasure.status}`;
    status.textContent = treasure.status.toUpperCase();
    
    header.appendChild(gemIcon);
    header.appendChild(status);

    // Create main content
    const content = document.createElement('div');
    content.className = 'treasure-content';

    // Price section with same styling as other info fields
    const priceSection = document.createElement('div');
    priceSection.className = 'price-section';
    
    const priceLabel = document.createElement('div');
    priceLabel.className = 'price-label';
    priceLabel.textContent = 'Investment:';
    
    const price = document.createElement('div');
    price.className = 'price';
    price.textContent = `$${(treasure.purchase_price / 100).toFixed(2)}`;
    
    priceSection.appendChild(priceLabel);
    priceSection.appendChild(price);

    // Purchase date
    const date = document.createElement('div');
    date.className = 'purchase-date';
    date.innerHTML = `<span class="date-label">Acquired:</span> ${new Date(treasure.created_at).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })}`;

    // Treasure ID
    const id = document.createElement('div');
    id.className = 'treasure-id';
    id.innerHTML = `<span class="id-label">Token ID:</span> ${treasure.id.slice(0, 8).toUpperCase()}...`;

    // Custom reward text section
    const rewardSection = document.createElement('div');
    rewardSection.className = 'reward-section';
    
    const rewardLabel = document.createElement('div');
    rewardLabel.className = 'reward-label';
    rewardLabel.innerHTML = 'Custom Reward Message <span class="required">*</span>';
    
    const rewardContent = document.createElement('div');
    rewardContent.className = 'reward-content';
    
    if (treasure.status === 'draft') {
      // Editable textarea for draft status
      const rewardTextarea = document.createElement('textarea');
      rewardTextarea.className = 'reward-textarea';
      rewardTextarea.placeholder = 'Enter a custom reward message for treasure hunters (required to publish)...';
      rewardTextarea.value = treasure.custom_merchant_reward_text || '';
      rewardTextarea.rows = 3;
      rewardTextarea.maxLength = 500;
      rewardTextarea.required = true;
      
      // Add required note
      const requiredNote = document.createElement('div');
      requiredNote.className = 'reward-required-note';
      requiredNote.textContent = 'This field is required to publish your merchant token';
      
      const saveButton = document.createElement('button');
      saveButton.className = 'save-reward-btn';
      saveButton.textContent = '💾 Save Message';
      saveButton.disabled = true;
      
      // Validate required field and enable/disable save button
      const validateAndUpdateButton = () => {
        const currentValue = rewardTextarea.value;
        const originalValue = treasure.custom_merchant_reward_text || '';
        const hasChanged = currentValue !== originalValue;
        const hasValue = currentValue.trim().length > 0;
        
        console.log('Validation check:', {
          currentValue,
          originalValue,
          hasChanged,
          hasValue,
          buttonWillBeDisabled: !hasChanged || !hasValue
        });
        
        // Only require change if there was originally no value, otherwise just require value
        const shouldEnableButton = hasValue && (hasChanged || !originalValue);
        saveButton.disabled = !shouldEnableButton;
        
        // Update textarea styling based on validation
        if (rewardTextarea.value.trim().length === 0) {
          rewardTextarea.classList.add('invalid');
        } else {
          rewardTextarea.classList.remove('invalid');
        }
      };
      
      // Enable save button when text changes and is valid
      rewardTextarea.addEventListener('input', validateAndUpdateButton);
      rewardTextarea.addEventListener('blur', validateAndUpdateButton);
      
      // Initial validation
      validateAndUpdateButton();
      
      // Save functionality
      saveButton.addEventListener('click', async () => {
        console.log('Save button clicked!');
        
        const trimmedValue = rewardTextarea.value.trim();
        if (trimmedValue.length === 0) {
          alert('Custom reward message is required and cannot be empty.');
          return;
        }
        
        console.log('About to save reward text:', trimmedValue);
        
        try {
          await this.saveCustomRewardText(treasure.id, trimmedValue, saveButton);
          
          // Only update local object if database save succeeded
          treasure.custom_merchant_reward_text = trimmedValue;
          validateAndUpdateButton();
          
          // Re-render the entire card to show the publishing section
          await this.reRenderTreasureCard(treasure);
        } catch (error) {
          console.error('Save failed, not updating local object:', error);
          // Don't update the local object if save failed
        }
      });
      
      rewardContent.appendChild(rewardTextarea);
      rewardContent.appendChild(requiredNote);
      rewardContent.appendChild(saveButton);
    } else {
      // Read-only display for non-draft status
      const rewardDisplay = document.createElement('div');
      rewardDisplay.className = 'reward-display';
      rewardDisplay.textContent = treasure.custom_merchant_reward_text || 'No custom message set';
      rewardContent.appendChild(rewardDisplay);
      
      const lockNote = document.createElement('div');
      lockNote.className = 'lock-note';
      lockNote.innerHTML = '🔒 Custom message can only be edited while in draft status';
      rewardContent.appendChild(lockNote);
    }
    
    rewardSection.appendChild(rewardLabel);
    rewardSection.appendChild(rewardContent);

    // Publishing section - only show for draft tokens with custom text
    const publishingSection = await this.renderPublishingSection(treasure);

    // Assemble the card
    content.appendChild(priceSection);
    content.appendChild(date);
    content.appendChild(id);
    content.appendChild(rewardSection);
    if (publishingSection) {
      content.appendChild(publishingSection);
    }
    
    card.appendChild(header);
    card.appendChild(content);

    return card;
  }

  private async renderPublishingSection(treasure: Treasure): Promise<HTMLElement | null> {
    // Only show publishing section for treasures with custom reward text
    if (!treasure.custom_merchant_reward_text || treasure.custom_merchant_reward_text.trim().length === 0) {
      return null;
    }

    const section = document.createElement('div');
    section.className = 'publishing-section';

    const label = document.createElement('div');
    label.className = 'publishing-label';
    label.textContent = 'Merchant Token Publishing';

    const content = document.createElement('div');
    content.className = 'publishing-content';

    if (treasure.published) {
      // Show published status
      const publishedInfo = document.createElement('div');
      publishedInfo.className = 'published-info';
      publishedInfo.innerHTML = `
        <div class="published-status">✅ Published</div>
        <div class="published-details">
          Published on: ${new Date(treasure.published_at!).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
          })}
        </div>
        <div class="published-location">
          Location: ${treasure.lat?.toFixed(6)}, ${treasure.lng?.toFixed(6)}
        </div>
      `;
      content.appendChild(publishedInfo);
    } else if (treasure.status === 'scheduled') {
      // Show scheduled status (now based on status field, not just publish_at)
      const scheduledInfo = document.createElement('div');
      scheduledInfo.className = 'scheduled-info';
      
      // Handle both old single city and new multiple cities format
      let locationDisplay = '';
      if (treasure.publish_city_ids && treasure.publish_city_ids.length > 0) {
        // New format: multiple cities - fetch actual city names
        try {
          const cityLookup = await this.treasureService.getCityNamesByIds(treasure.publish_city_ids);
          const cityNames = treasure.publish_city_ids
            .map(cityId => cityLookup[cityId] || `City ID ${cityId}`)
            .join(', ');
          
          locationDisplay = `Places: ${cityNames} (${treasure.publish_city_ids.length} location${treasure.publish_city_ids.length !== 1 ? 's' : ''})`;
        } catch (error) {
          console.error('Error fetching city names:', error);
          locationDisplay = `Places: ${treasure.publish_city_ids.length} location${treasure.publish_city_ids.length !== 1 ? 's' : ''} selected`;
        }
      } else if (treasure.publish_city_id) {
        // Legacy format: single city
        const cityName = this.availableCities.find(city => city.id === treasure.publish_city_id)?.name || 'Unknown City';
        locationDisplay = `${this.placeTypeLabel}: ${cityName}`;
      } else {
        locationDisplay = 'Location: Not specified';
      }
      
      scheduledInfo.innerHTML = `
        <div class="scheduled-status">⏰ Scheduled for Publishing</div>
        <div class="scheduled-details">
          ${treasure.publish_at ? `
            Publish Time: ${new Date(treasure.publish_at).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              timeZoneName: 'short'
            })}
            <br><span style="font-size: 0.9em; color: #666;">
              (Your local time: ${Intl.DateTimeFormat().resolvedOptions().timeZone})
            </span>
          ` : 'Publishing time not set'}
        </div>
        <div class="scheduled-city">${locationDisplay}</div>
      `;

      const cancelButton = document.createElement('button');
      cancelButton.className = 'cancel-publish-btn';
      cancelButton.textContent = 'Cancel Publishing';
      cancelButton.addEventListener('click', () => this.cancelPublishing(treasure, section));

      scheduledInfo.appendChild(cancelButton);
      content.appendChild(scheduledInfo);
    } else if (treasure.status === 'draft') {
      // Show hierarchical scheduling form
      const schedulingForm = document.createElement('div');
      schedulingForm.className = 'scheduling-form';

      // Step 1: Municipality selection
      const municipalityLabel = document.createElement('label');
      municipalityLabel.textContent = `Step 1: Select ${this.placeTypeLabel}:`;
      municipalityLabel.className = 'form-label';

      const municipalitySelect = document.createElement('select');
      municipalitySelect.className = 'municipality-select';
      municipalitySelect.innerHTML = `<option value="">Choose a ${this.placeTypeLabel.toLowerCase()}...</option>`;
      
      this.availableCities.forEach(municipality => {
        const option = document.createElement('option');
        option.value = municipality.id.toString();
        option.textContent = municipality.name;
        municipalitySelect.appendChild(option);
      });

      // Step 2: Cities within municipality (initially hidden)
      const citiesContainer = document.createElement('div');
      citiesContainer.className = 'cities-container';
      citiesContainer.style.display = 'none';

      const citiesLabel = document.createElement('label');
      citiesLabel.textContent = 'Step 2: Select places to publish in:';
      citiesLabel.className = 'form-label';

      const citiesCheckboxContainer = document.createElement('div');
      citiesCheckboxContainer.className = 'cities-checkbox-container';

      const selectAllCheckbox = document.createElement('label');
      selectAllCheckbox.className = 'select-all-option';
      selectAllCheckbox.innerHTML = `
        <input type="checkbox" class="select-all-checkbox"> 
        <strong>Select All</strong>
      `;

      citiesContainer.appendChild(citiesLabel);
      citiesContainer.appendChild(selectAllCheckbox);
      citiesContainer.appendChild(citiesCheckboxContainer);

      // Municipality selection handler
      municipalitySelect.addEventListener('change', async () => {
        const municipalityId = municipalitySelect.value;
        
        if (municipalityId) {
          // Load cities in this municipality
          try {
            citiesCheckboxContainer.innerHTML = '<div class="loading-cities">🔍 Searching for places in this municipality...</div>';
            citiesContainer.style.display = 'block';

            this.selectedMunicipalityCities = await this.treasureService.getCitiesInMunicipality(parseInt(municipalityId));
            
            citiesCheckboxContainer.innerHTML = '';
            
            if (this.selectedMunicipalityCities.length === 0) {
              citiesCheckboxContainer.innerHTML = `
                <div class="no-cities">
                  <p>🏘️ No sub-places found for this municipality.</p>
                  <p style="font-size: 0.9em; color: #666;">
                    This might be because:<br>
                    • The municipality is small and doesn't have distinct sub-areas<br>
                    • Data is not available in OpenStreetMap<br>
                    • The area boundary data is incomplete
                  </p>
                  <p style="font-size: 0.9em; color: #666;">
                    You can still schedule publishing - tokens will be placed randomly within the municipality boundary.
                  </p>
                </div>
              `;
            } else {
              this.selectedMunicipalityCities.forEach(city => {
                const checkboxLabel = document.createElement('label');
                checkboxLabel.className = 'city-checkbox-option';
                checkboxLabel.innerHTML = `
                  <input type="checkbox" value="${city.id}" class="city-checkbox">
                  <span class="city-name">${city.name}</span>
                `;
                citiesCheckboxContainer.appendChild(checkboxLabel);
              });

              // Select all functionality
              const selectAllCheckboxInput = selectAllCheckbox.querySelector('.select-all-checkbox') as HTMLInputElement;
              const cityCheckboxes = citiesCheckboxContainer.querySelectorAll('.city-checkbox') as NodeListOf<HTMLInputElement>;

              selectAllCheckboxInput.addEventListener('change', () => {
                cityCheckboxes.forEach(checkbox => {
                  checkbox.checked = selectAllCheckboxInput.checked;
                });
                validateForm();
              });

              cityCheckboxes.forEach(checkbox => {
                checkbox.addEventListener('change', () => {
                  // Update select all checkbox state
                  const checkedCount = Array.from(cityCheckboxes).filter(cb => cb.checked).length;
                  selectAllCheckboxInput.checked = checkedCount === cityCheckboxes.length;
                  selectAllCheckboxInput.indeterminate = checkedCount > 0 && checkedCount < cityCheckboxes.length;
                  validateForm();
                });
              });
            }
          } catch (error) {
            console.error('Error loading cities:', error);
            citiesCheckboxContainer.innerHTML = '<div class="error-cities">Error loading places. Please try again.</div>';
          }
        } else {
          citiesContainer.style.display = 'none';
          this.selectedMunicipalityCities = [];
        }
        
        validateForm();
      });

      // Step 3: Date/time selection with clear timezone handling
      const timeLabel = document.createElement('label');
      timeLabel.innerHTML = `
        Step 3: Publish Date & Time:
        <span style="font-size: 0.8em; color: #666; font-weight: normal;">
          (Your local time: ${Intl.DateTimeFormat().resolvedOptions().timeZone})
        </span>
      `;
      timeLabel.className = 'form-label';

      const datetimeInput = document.createElement('input');
      datetimeInput.type = 'datetime-local';
      datetimeInput.className = 'datetime-input';
      
      // Set minimum time to current local time (more intuitive)
      const now = new Date();
      const localISOString = new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString();
      datetimeInput.min = localISOString.slice(0, 16);

      // Add timezone explanation
      const timezoneNote = document.createElement('div');
      timezoneNote.className = 'timezone-note';
      timezoneNote.style.fontSize = '0.85em';
      timezoneNote.style.color = '#666';
      timezoneNote.style.marginTop = '4px';
      timezoneNote.textContent = '💡 Time will be converted to UTC for scheduling. Publishers worldwide will see the correct local time.';

      const scheduleButton = document.createElement('button');
      scheduleButton.className = 'schedule-publish-btn';
      scheduleButton.textContent = 'Schedule Publishing';
      scheduleButton.disabled = true;

      // Add helper text
      const helperText = document.createElement('div');
      helperText.className = 'form-helper';
      helperText.textContent = 'Complete all steps to schedule publishing';
      helperText.style.fontSize = '0.9em';
      helperText.style.color = '#666';
      helperText.style.marginTop = '8px';

      // Form validation
      const validateForm = () => {
        const municipalitySelected = !!municipalitySelect.value;
        const cityCheckboxes = citiesCheckboxContainer.querySelectorAll('.city-checkbox:checked') as NodeListOf<HTMLInputElement>;
        const citiesSelected = cityCheckboxes.length > 0;
        const timeSelected = !!datetimeInput.value;
        
        const isValid = municipalitySelected && citiesSelected && timeSelected;
        scheduleButton.disabled = !isValid;
        
        if (isValid) {
          helperText.textContent = `Ready to schedule publishing in ${cityCheckboxes.length} place(s)!`;
          helperText.style.color = '#22c55e';
        } else if (!municipalitySelected) {
          helperText.textContent = `Select a ${this.placeTypeLabel.toLowerCase()} to continue`;
          helperText.style.color = '#666';
        } else if (!citiesSelected) {
          helperText.textContent = 'Select at least one place within the municipality';
          helperText.style.color = '#666';
        } else if (!timeSelected) {
          helperText.textContent = 'Select a publish date and time';
          helperText.style.color = '#666';
        }
      };

      datetimeInput.addEventListener('change', validateForm);

      scheduleButton.addEventListener('click', () => {
        const cityCheckboxes = citiesCheckboxContainer.querySelectorAll('.city-checkbox:checked') as NodeListOf<HTMLInputElement>;
        const selectedCityIds = Array.from(cityCheckboxes).map(cb => parseInt(cb.value));
        
        // Convert local datetime-local input to proper ISO string
        const localDateTime = datetimeInput.value; // This is in YYYY-MM-DDTHH:mm format (local time)
        const publishTime = new Date(localDateTime).toISOString(); // This converts to UTC
        
        this.schedulePublishing(treasure, selectedCityIds, publishTime, section);
      });

      schedulingForm.appendChild(municipalityLabel);
      schedulingForm.appendChild(municipalitySelect);
      schedulingForm.appendChild(citiesContainer);
      schedulingForm.appendChild(timeLabel);
      schedulingForm.appendChild(datetimeInput);
      schedulingForm.appendChild(timezoneNote);
      schedulingForm.appendChild(helperText);
      schedulingForm.appendChild(scheduleButton);

      content.appendChild(schedulingForm);
    } else {
      // Non-draft status without publishing info
      const unavailableInfo = document.createElement('div');
      unavailableInfo.className = 'publishing-unavailable';
      unavailableInfo.textContent = 'Publishing is only available for draft tokens.';
      content.appendChild(unavailableInfo);
    }

    section.appendChild(label);
    section.appendChild(content);

    return section;
  }

  private async schedulePublishing(treasure: Treasure, selectedCityIds: number[], publishTime: string, section: HTMLElement): Promise<void> {
    const button = section.querySelector('.schedule-publish-btn') as HTMLButtonElement;
    const originalText = button.textContent;
    
    button.disabled = true;
    button.textContent = 'Scheduling...';

    try {
      await this.treasureService.scheduleTokenPublishing(treasure.id, publishTime, selectedCityIds);
      
      // Update treasure object
      treasure.status = 'scheduled'; // Update status to scheduled
      treasure.publish_at = publishTime;
      treasure.publish_city_ids = selectedCityIds;

      // Re-render this section
      const newSection = await this.renderPublishingSection(treasure);
      if (newSection) {
        section.replaceWith(newSection);
      }

      // Show success feedback
      const successMsg = document.createElement('div');
      successMsg.className = 'success-message';
      successMsg.textContent = '✅ Publishing scheduled successfully!';
      newSection?.appendChild(successMsg);
      
      setTimeout(() => {
        successMsg.remove();
      }, 3000);

    } catch (error) {
      console.error('Failed to schedule publishing:', error);
      
      button.textContent = '❌ Failed';
      button.style.background = '#ef4444';
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to schedule publishing: ${errorMessage}`);
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = '';
        button.disabled = false;
      }, 3000);
    }
  }

  private async cancelPublishing(treasure: Treasure, section: HTMLElement): Promise<void> {
    const button = section.querySelector('.cancel-publish-btn') as HTMLButtonElement;
    const originalText = button.textContent;
    
    button.disabled = true;
    button.textContent = 'Cancelling...';

    try {
      await this.treasureService.cancelTokenPublishing(treasure.id);
      
      // Update treasure object
      treasure.status = 'draft'; // Revert status back to draft
      treasure.publish_at = undefined;
      treasure.publish_city_id = undefined;
      treasure.publish_city_ids = undefined; // Also clear new array field

      // Re-render this section
      const newSection = await this.renderPublishingSection(treasure);
      if (newSection) {
        section.replaceWith(newSection);
      }

      // Show success feedback
      const successMsg = document.createElement('div');
      successMsg.className = 'success-message';
      successMsg.textContent = '✅ Publishing cancelled successfully!';
      newSection?.appendChild(successMsg);
      
      setTimeout(() => {
        successMsg.remove();
      }, 3000);

    } catch (error) {
      console.error('Failed to cancel publishing:', error);
      
      button.textContent = '❌ Failed';
      button.style.background = '#ef4444';
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to cancel publishing: ${errorMessage}`);
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = '';
        button.disabled = false;
      }, 3000);
    }
  }

  private async reRenderTreasureCard(treasure: Treasure): Promise<void> {
    // Find the existing card element
    const existingCard = document.querySelector(`[data-treasure-id="${treasure.id}"]`) as HTMLElement;
    if (existingCard) {
      // Create new card and replace the old one
      const newCard = await this.renderTreasureCard(treasure);
      existingCard.replaceWith(newCard);
    }
  }

  private async saveCustomRewardText(treasureId: string, rewardText: string, button: HTMLButtonElement): Promise<void> {
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = '💾 Saving...';
    
    try {
      await this.treasureService.updateCustomRewardText(treasureId, rewardText);
      
      // Show success feedback
      button.textContent = '✅ Saved!';
      button.style.background = '#10b981';
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = '';
      }, 2000);
      
    } catch (error) {
      console.error('Failed to save custom reward text:', error);
      
      // Show error feedback
      button.textContent = '❌ Failed';
      button.style.background = '#ef4444';
      button.disabled = false;
      
      // Show user-friendly error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to save custom reward text: ${errorMessage}`);
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = '';
      }, 3000);
    }
  }

  async refreshInventory(partnerId: string, container?: HTMLElement): Promise<void> {
    if (container) {
      console.log('Refreshing treasure inventory for partner:', partnerId);
      await this.loadAndRender(partnerId, container, undefined, true); // Force refresh
    } else {
      // If no container provided, just reload the data with force refresh
      await this.loadTreasures(partnerId, true);
    }
  }
}
